import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Audio } from 'expo-av';
import { Platform } from 'react-native';
import {
  cacheDirectory,
  writeAsStringAsync,
  EncodingType,
} from 'expo-file-system';
import { speechGoogle } from 'utils/misc';

const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const [soundObject] = useState(new Audio.Sound());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  useEffect(() => {
    Audio.setAudioModeAsync({
      playsInSilentModeIOS: true,
    });

    return () => {
      soundObject.unloadAsync();
    };
  }, []);

  const onPlaybackStatusUpdate = useCallback(async (playbackStatus) => {
    if (!playbackStatus.isLoaded) {
      if (playbackStatus.error) {
        console.log(
          `Encountered a fatal error during playback: ${playbackStatus.error}`
        );
      }
    } else {
      setIsPlaying(playbackStatus.isPlaying);
      setIsBuffering(playbackStatus.isBuffering);

      if (playbackStatus.didJustFinish && playbackStatus.isLoaded) {
        try {
          await soundObject.setStatusAsync({
            positionMillis: 0,
            shouldPlay: false,
          });
        } catch (error) {
          console.log('too fast');
        }
      }
    }
  }, []);

  useEffect(() => {
    soundObject.setOnPlaybackStatusUpdate(onPlaybackStatusUpdate);
  }, [onPlaybackStatusUpdate]);

  const loadSoundURI = useCallback(
    async (uri) => {
      await soundObject.unloadAsync();
      if (!soundObject._loading) {
        await soundObject.loadAsync({ uri: uri.uri });
      }
    },
    [soundObject]
  );

  const loadSoundWord = useCallback(async (selectedWord) => {
    let word = selectedWord.traditional;
    await loadSound(word);
  }, []);

  const loadSoundSentence = useCallback(async (selectedSentence) => {
    let sentence = selectedSentence.sentence;
    loadSound(sentence);
  }, []);

  let loadSoundPromise = Promise.resolve();

  const loadSound = useCallback(
    async (text) => {
      if (typeof text === 'undefined') {
        return;
      }

      loadSoundPromise = loadSoundPromise.then(async () => {
        let response = await speechGoogle(text);
        await soundObject.unloadAsync();
        try {
          if (Platform.OS === 'web') {
            await soundObject.loadAsync({
              uri: 'data:audio/wav;base64,' + response.data.audioContent,
            });
          } else if (Platform.OS !== 'web') {
            let uri = cacheDirectory + 'tempsoundfile.mp3';
            await writeAsStringAsync(uri, response.data.audioContent, {
              encoding: EncodingType.Base64,
            });
            await soundObject.loadAsync({ uri: uri });
          }
        } catch (error) {
          console.log('error');
          console.log(error);
          console.log('probably clicked too fast');
        }
      });
    },
    [soundObject]
  );

  const unloadSound = useCallback(async () => {
    try {
      await soundObject.unloadAsync();
    } catch (error) {
      console.log('error');
      console.log(error);
    }
  }, [soundObject]);

  const speak = useCallback(async () => {
    try {
      await soundObject.setStatusAsync({ shouldPlay: true });
    } catch (error) {
      console.log('error');
      console.log(error);
    }
  }, [soundObject]);

  const value = {
    soundObject,
    isPlaying,
    isBuffering,
    loadSoundURI,
    loadSoundWord,
    loadSoundSentence,
    loadSound,
    unloadSound,
    speak,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
};
